import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { GALLERY } from "@/data/content";
import { useSiteContent } from "@/context/SiteContentContext";
import { Reveal, Overline } from "@/components/site/Reveal";

export default function Gallery() {
  const { gallery } = useSiteContent();
  const [active, setActive] = useState(null);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative bg-charcoal py-24 lg:py-36"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <Reveal>
              <Overline className="mb-6">The Work</Overline>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream uppercase tracking-tight">
                Gallery
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-cream/55 text-sm font-light leading-relaxed">
              Glossy paint, chromed detail and showroom finishes — a look at
              motorcycles given the MOTO LUXE treatment.
            </p>
          </Reveal>
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {gallery.map((g, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08} className="mb-5 break-inside-avoid">
              <button
                data-testid={`gallery-item-${i}`}
                onClick={() => setActive(g)}
                className="group relative block w-full overflow-hidden border border-white/5 hover:border-gold/30 transition-colors duration-500"
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  loading="lazy"
                  className={`w-full object-cover transition-transform duration-700 group-hover:scale-[1.06] ${
                    i % 3 === 1 ? "h-[420px]" : "h-[300px]"
                  }`}
                />
                <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/30 transition-colors duration-500" />
                <span className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500 font-mono text-[10px] tracking-[0.2em] text-gold uppercase">
                  {g.alt}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[60] bg-ink/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
          >
            <button
              data-testid="lightbox-close"
              className="absolute top-6 right-6 text-cream/70 hover:text-gold transition-colors"
              onClick={() => setActive(null)}
            >
              <X size={28} />
            </button>
            <motion.img
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              src={active.src}
              alt={active.alt}
              className="max-h-[85vh] max-w-full object-contain border border-gold/20"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
